import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Star, ChevronRight, ChevronsLeft, ChevronsRight, Download, Crown, MessageCircle, Sparkles, Loader2, CheckCircle2, Pencil, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';

const HEALTH_PLAN_OPTIONS = ['Particular', 'Hapvida', 'Bradesco', 'SulAmérica', 'Unimed', 'NotreDame', 'Amil', 'Outros'];

function normalizeHealthPlans(plans) {
  const values = Array.isArray(plans) ? plans.filter(Boolean) : [];
  return ['Particular', ...values.filter((plan) => plan !== 'Particular')];
}

export default function AdminDoctors() {
  const { api, triggerDownload } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [bioCoverage, setBioCoverage] = useState(null);
  const [regeneratingIds, setRegeneratingIds] = useState(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    tipo: '',
    specialty: '',
    address: '',
    city: '',
    state: '',
    phone: '',
    min_rating: '',
    plans: '',
    premium: '',
    has_whatsapp: '',
    has_bio: '',
    created_at: '',
  });

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
    setPage(1);
  };

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      const res = await api.get(`/admin/doctors?${params.toString()}`);
      setDoctors(res.data.doctors || []);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch {}
    finally {
      setLoading(false);
    }
  }, [api, page, filters]);

  const fetchBioCoverage = useCallback(async () => {
    try {
      const res = await api.get('/admin/doctors/bio-coverage');
      setBioCoverage(res.data);
    } catch {}
  }, [api]);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);
  useEffect(() => { fetchBioCoverage(); }, [fetchBioCoverage]);

  const openEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
    setEditForm({
      full_name: doctor.full_name || '',
      specialty_name: doctor.specialty_name || '',
      crm: doctor.crm || '',
      crm_state: doctor.crm_state || '',
      rqe: doctor.rqe || '',
      address: doctor.address || '',
      neighborhood: doctor.neighborhood || '',
      city: doctor.city || '',
      state: doctor.state || '',
      zip_code: doctor.zip_code || '',
      phone: doctor.phone || '',
      whatsapp: doctor.whatsapp || '',
      bio: doctor.bio || '',
      formacao: doctor.formacao || '',
      experiencia: doctor.experiencia || '',
      accepted_health_plans: normalizeHealthPlans(doctor.accepted_health_plans),
    });
  };

  const updateEditField = (field, value) => {
    setEditForm((current) => ({ ...current, [field]: value }));
  };

  const saveDoctorEdit = async () => {
    if (!editingDoctor) return;
    setSavingEdit(true);
    try {
      const res = await api.patch(`/admin/doctors/${editingDoctor.id}`, {
        ...editForm,
        accepted_health_plans: normalizeHealthPlans(editForm.accepted_health_plans),
      });
      const updated = res.data;
      setDoctors((prev) => prev.map((doc) => doc.id === updated.id ? { ...doc, ...updated } : doc));
      setEditingDoctor(null);
      setEditForm({});
      toast.success('Perfil médico atualizado');
      fetchBioCoverage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar perfil médico');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleEditHealthPlan = (plan) => {
    if (plan === 'Particular') return;
    setEditForm((current) => {
      const currentPlans = normalizeHealthPlans(current.accepted_health_plans);
      const nextPlans = currentPlans.includes(plan)
        ? currentPlans.filter((item) => item !== plan)
        : [...currentPlans, plan];
      return { ...current, accepted_health_plans: normalizeHealthPlans(nextPlans) };
    });
  };

  const regenerateBio = async (id) => {
    setRegeneratingIds(prev => new Set(prev).add(id));
    try {
      const res = await api.post(`/admin/doctors/${id}/regenerate-bio`);
      setDoctors(prev => prev.map(doc => doc.id === id ? { ...doc, bio: res.data.bio } : doc));
      toast.success(`Bio regenerada (${res.data.bio_length} chars)`);
      fetchBioCoverage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao regenerar bio');
    } finally {
      setRegeneratingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const regenerateBatch = async () => {
    if (batchRunning) return;
    setBatchRunning(true);
    toast.info('Gerando lote de 10 bios... (~2 min)');
    try {
      const res = await api.post('/admin/doctors/regenerate-bios-batch', { limit: 10, only_missing: true });
      toast.success(`${res.data.generated} bios geradas, ${res.data.failed} falharam de ${res.data.processed}`);
      fetchBioCoverage();
      fetchDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha no lote');
    } finally {
      setBatchRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-doctors">
      <Navbar />
      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Base de CNPJ</h1>
            <p className="text-slate-600 mt-1">{total} registros cadastrados</p>
            {bioCoverage && (
              <div className="mt-2 flex items-center gap-2 text-xs" data-testid="bio-coverage">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-slate-600">
                  Bios SEO: <span className="font-semibold text-emerald-700">{bioCoverage.with_bio.toLocaleString('pt-BR')}</span> de {bioCoverage.total.toLocaleString('pt-BR')} ({bioCoverage.coverage_pct}%)
                </span>
                <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${bioCoverage.coverage_pct}%` }} />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={regenerateBatch}
              disabled={batchRunning}
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              data-testid="regenerate-bios-batch-btn"
            >
              {batchRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              Gerar 10 bios
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerDownload('/admin/export-cnpjs', `cnpjs_backup_${new Date().toISOString().slice(0,10)}.xlsx`)}
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              data-testid="backup-cnpjs-btn"
            >
              <Download className="w-4 h-4 mr-1.5" /> Backup Base CNPJ
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="doctors-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Nome</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Tipo</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Especialidade</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Endereço</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Cidade</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Estado</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Telefone</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Avaliações</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Planos</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-600">Premium</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">WhatsApp</th>
                  <th className="px-4 py-3 text-center font-medium text-slate-600">Bio SEO</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Data Cadastro</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">Ações</th>
                </tr>
                <tr className="border-t border-slate-100 bg-white">
                  <th className="px-4 py-2">
                    <input value={filters.name} onChange={(e) => updateFilter('name', e.target.value)} placeholder="Filtrar nome" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <select value={filters.tipo} onChange={(e) => updateFilter('tipo', e.target.value)} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary">
                      <option value="">Todos</option>
                      <option value="medico">Médico</option>
                      <option value="clinica">Clínica</option>
                    </select>
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.specialty} onChange={(e) => updateFilter('specialty', e.target.value)} placeholder="Especialidade" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.address} onChange={(e) => updateFilter('address', e.target.value)} placeholder="Endereço" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.city} onChange={(e) => updateFilter('city', e.target.value)} placeholder="Cidade" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.state} onChange={(e) => updateFilter('state', e.target.value.toUpperCase().slice(0, 2))} placeholder="UF" className="w-16 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal uppercase outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.phone} onChange={(e) => updateFilter('phone', e.target.value)} placeholder="Telefone" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <select value={filters.min_rating} onChange={(e) => updateFilter('min_rating', e.target.value)} className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary">
                      <option value="">Todas</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                      <option value="4.5">4.5+</option>
                    </select>
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.plans} onChange={(e) => updateFilter('plans', e.target.value)} placeholder="Plano" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2">
                    <select value={filters.premium} onChange={(e) => updateFilter('premium', e.target.value)} className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary">
                      <option value="">Todos</option>
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </select>
                  </th>
                  <th className="px-4 py-2">
                    <select value={filters.has_whatsapp} onChange={(e) => updateFilter('has_whatsapp', e.target.value)} className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary">
                      <option value="">Todos</option>
                      <option value="true">Com</option>
                      <option value="false">Sem</option>
                    </select>
                  </th>
                  <th className="px-4 py-2">
                    <select value={filters.has_bio} onChange={(e) => updateFilter('has_bio', e.target.value)} className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary">
                      <option value="">Todos</option>
                      <option value="true">Com</option>
                      <option value="false">Sem</option>
                    </select>
                  </th>
                  <th className="px-4 py-2">
                    <input value={filters.created_at} onChange={(e) => updateFilter('created_at', e.target.value)} placeholder="Data" className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-normal outline-none focus:border-primary" />
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={14} className="px-4 py-12 text-center text-slate-400">Carregando...</td></tr>
                ) : doctors.length === 0 ? (
                  <tr><td colSpan={14} className="px-4 py-12 text-center text-slate-400">Nenhum registro encontrado</td></tr>
                ) : doctors.map((d) => (
                  <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`doctor-row-${d.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900 max-w-[180px] break-words">{d.full_name}</td>
                    <td className="px-4 py-3">
                      <Badge variant={d.tipo === 'clinica' ? 'default' : 'secondary'} className="text-xs">
                        {d.tipo === 'clinica' ? 'Clínica' : 'Médico'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {d.specialty_name ? <Badge variant="secondary" className="text-xs">{d.specialty_name}</Badge> : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{d.address || '-'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.city || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{d.state || '-'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.phone || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {d.rating_avg > 0 ? (
                        <span className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="font-medium">{Number(d.rating_avg).toFixed(1)}</span>
                          <span className="text-slate-400 text-xs">({d.rating_count})</span>
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      {d.accepted_health_plans?.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {d.accepted_health_plans.slice(0, 3).map((p) => (
                            <Badge key={p} variant="outline" className="text-xs whitespace-nowrap">{p}</Badge>
                          ))}
                          {d.accepted_health_plans.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{d.accepted_health_plans.length - 3}</Badge>
                          )}
                        </div>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={async () => {
                          try {
                            await api.patch(`/admin/doctors/${d.id}/premium`, { is_premium: !d.is_premium });
                            setDoctors(prev => prev.map(doc => doc.id === d.id ? { ...doc, is_premium: !doc.is_premium } : doc));
                          } catch {}
                        }}
                        className={`p-1.5 rounded-lg transition-colors ${d.is_premium ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-300 hover:text-slate-500'}`}
                        title={d.is_premium ? 'Remover Premium' : 'Marcar como Premium'}
                        data-testid={`premium-toggle-${d.id}`}
                      >
                        <Crown className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          placeholder="5511999..."
                          defaultValue={d.whatsapp || ''}
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val !== (d.whatsapp || '')) {
                              try {
                                await api.patch(`/admin/doctors/${d.id}/whatsapp`, { whatsapp: val });
                                setDoctors(prev => prev.map(doc => doc.id === d.id ? { ...doc, whatsapp: val } : doc));
                              } catch {}
                            }
                          }}
                          className="w-28 text-xs px-2 py-1 border border-slate-200 rounded-md focus:border-primary focus:outline-none"
                          data-testid={`whatsapp-input-${d.id}`}
                        />
                        {d.whatsapp && <MessageCircle className="w-3.5 h-3.5 text-green-500" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => regenerateBio(d.id)}
                        disabled={regeneratingIds.has(d.id)}
                        className={`p-1.5 rounded-lg transition-colors ${d.bio ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' : 'bg-slate-100 text-slate-400 hover:text-slate-600'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={d.bio ? `Bio existe (${d.bio.length} chars) — clique para regenerar` : 'Gerar bio SEO via LLM'}
                        data-testid={`regenerate-bio-${d.id}`}
                      >
                        {regeneratingIds.has(d.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : d.bio ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDoctor(d)}
                        className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`edit-doctor-${d.id}`}
                      >
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <span className="text-sm text-slate-500">
              Página <span className="font-semibold text-slate-700">{page}</span> de <span className="font-semibold text-slate-700">{totalPages}</span>
              <span className="text-slate-400 ml-2">({total} registros)</span>
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(1)} title="Primeira">
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)} title="Anterior">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {(() => {
                const pages = [];
                let start = Math.max(1, page - 2);
                let end = Math.min(totalPages, start + 4);
                if (end - start < 4) start = Math.max(1, end - 4);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <Button
                    key={p}
                    variant={p === page ? "default" : "outline"}
                    size="icon"
                    className={`h-8 w-8 text-xs ${p === page ? 'pointer-events-none' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Button>
                ));
              })()}
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} title="Próxima">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Última">
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <Dialog open={!!editingDoctor} onOpenChange={(open) => {
          if (!open) {
            setEditingDoctor(null);
            setEditForm({});
          }
        }}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar perfil médico</DialogTitle>
              <DialogDescription>
                As alterações são salvas diretamente na base de CNPJ usada nas buscas públicas.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="admin-full-name">Nome público</Label>
                <Input id="admin-full-name" value={editForm.full_name || ''} onChange={(e) => updateEditField('full_name', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-specialty">Especialidade</Label>
                <Input id="admin-specialty" value={editForm.specialty_name || ''} onChange={(e) => updateEditField('specialty_name', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="admin-crm">CRM</Label>
                  <Input id="admin-crm" value={editForm.crm || ''} onChange={(e) => updateEditField('crm', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-crm-state">UF CRM</Label>
                  <Input id="admin-crm-state" value={editForm.crm_state || ''} onChange={(e) => updateEditField('crm_state', e.target.value.toUpperCase().slice(0, 2))} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-rqe">RQE</Label>
                <Input id="admin-rqe" value={editForm.rqe || ''} onChange={(e) => updateEditField('rqe', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-phone">Telefone</Label>
                <Input id="admin-phone" value={editForm.phone || ''} onChange={(e) => updateEditField('phone', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-whatsapp">WhatsApp</Label>
                <Input id="admin-whatsapp" value={editForm.whatsapp || ''} onChange={(e) => updateEditField('whatsapp', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-zip">CEP</Label>
                <Input id="admin-zip" value={editForm.zip_code || ''} onChange={(e) => updateEditField('zip_code', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-state">Estado</Label>
                <Input id="admin-state" value={editForm.state || ''} onChange={(e) => updateEditField('state', e.target.value.toUpperCase().slice(0, 2))} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="admin-address">Endereço</Label>
                <Input id="admin-address" value={editForm.address || ''} onChange={(e) => updateEditField('address', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-neighborhood">Bairro</Label>
                <Input id="admin-neighborhood" value={editForm.neighborhood || ''} onChange={(e) => updateEditField('neighborhood', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-city">Cidade</Label>
                <Input id="admin-city" value={editForm.city || ''} onChange={(e) => updateEditField('city', e.target.value)} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="admin-bio">Sobre o profissional ou clínica</Label>
                <Textarea id="admin-bio" rows={4} value={editForm.bio || ''} onChange={(e) => updateEditField('bio', e.target.value)} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Planos atendidos</Label>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {HEALTH_PLAN_OPTIONS.map((plan) => {
                    const checked = normalizeHealthPlans(editForm.accepted_health_plans).includes(plan);
                    const locked = plan === 'Particular';
                    return (
                      <label
                        key={plan}
                        className={`flex min-h-10 items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                          checked
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          onChange={() => toggleEditHealthPlan(plan)}
                          className="h-4 w-4 accent-primary"
                        />
                        <span>{plan}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">Atendimento particular fica sempre ativo.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-formacao">Formação</Label>
                <Textarea id="admin-formacao" rows={4} value={editForm.formacao || ''} onChange={(e) => updateEditField('formacao', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-experiencia">Experiência</Label>
                <Textarea id="admin-experiencia" rows={4} value={editForm.experiencia || ''} onChange={(e) => updateEditField('experiencia', e.target.value)} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingDoctor(null)} disabled={savingEdit}>
                Cancelar
              </Button>
              <Button onClick={saveDoctorEdit} disabled={savingEdit} className="bg-primary">
                {savingEdit ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Salvar alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
